/**
 * Suggestion cards of one mailbox email (#3740): a new customer, the linked
 * customer's contact details, the work order the attachments belong to, a new
 * vehicle, a vehicle's mileage, the customer as a vehicle's work orderer, or a
 * work order for the work the email asks for (its time is picked here).
 *
 * Approving is two steps, like an assistant confirmation: Preview asks the
 * API to read the target as it is now and freeze a plan, the card shows each
 * change as current → new, and Confirm runs exactly that plan. Approve is
 * only offered to someone with the card's write permission; the API checks
 * it again.
 */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { api, ApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { useNotify } from '@/stores/notificationStore';
import type { EmailSuggestion } from '@/types';
import { formatDateTimeShort } from '@/utils/locale';

export type EmailSuggestionOutcome = 'accepted' | 'rejected' | 'expired';

export interface EmailSuggestionDecided {
  suggestionId: string;
  kind: EmailSuggestion['kind'];
  outcome: EmailSuggestionOutcome;
}

interface FieldChange {
  field: string;
  from: string | null;
  to: string | null;
}

interface Preview {
  planHash: string;
  fieldChanges: FieldChange[];
}

interface Candidate {
  bookingId: string;
  workOrderNumber: string | null;
  registrationNumber: string | null;
}

const FIELD_LABEL_KEYS: Record<string, string> = {
  firstName: 'emailSuggestionFieldFirstName',
  lastName: 'emailSuggestionFieldLastName',
  name: 'emailSuggestionFieldName',
  businessId: 'emailSuggestionFieldBusinessId',
  phone: 'emailSuggestionFieldPhone',
  email: 'emailSuggestionFieldEmail',
  workOrder: 'emailSuggestionFieldWorkOrder',
  file: 'emailSuggestionFieldFile',
  registrationNumber: 'emailSuggestionFieldRegistrationNumber',
  vin: 'emailSuggestionFieldVin',
  mileage: 'emailSuggestionFieldMileage',
  owner: 'emailSuggestionFieldOwner',
  vehicle: 'emailSuggestionFieldVehicle',
  orderer: 'emailSuggestionFieldOrderer',
  requestedWork: 'emailSuggestionFieldRequestedWork',
  requestedTime: 'emailSuggestionFieldRequestedTime',
  scheduledAt: 'emailSuggestionFieldScheduledAt',
};

/** Kinds this card list can show; others are left out. */
const SUPPORTED_KINDS: ReadonlySet<EmailSuggestion['kind']> = new Set([
  'create_party',
  'update_party_contact',
  'attach_to_work_order',
  'create_vehicle',
  'update_vehicle',
  'link_vehicle_party',
  'create_work_order',
]);

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function numberText(value: unknown): string | null {
  return typeof value === 'number' ? String(value) : null;
}

function present(entries: [string, string | null][]): [string, string][] {
  return entries.flatMap(([field, value]) => (value ? [[field, value] as [string, string]] : []));
}

function candidatesOf(suggestion: EmailSuggestion): Candidate[] {
  const raw = record(suggestion.proposed)['candidates'];
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    const c = record(item);
    const bookingId = text(c['bookingId']);
    return bookingId
      ? [{ bookingId, workOrderNumber: text(c['workOrderNumber']), registrationNumber: text(c['registrationNumber']) }]
      : [];
  });
}

/** The card's headline and the values it proposes, before any preview. */
function describe(suggestion: EmailSuggestion): { titleKey: string; titleParams?: Record<string, string>; values: [string, string][] } {
  const proposed = record(suggestion.proposed);
  if (suggestion.kind === 'create_party') {
    const values = record(proposed['values']);
    const company = proposed['partyType'] === 'company';
    const fields = company ? ['name', 'businessId', 'phone', 'email'] : ['firstName', 'lastName', 'phone', 'email'];
    return {
      titleKey: company ? 'emailSuggestionCreateCompany' : 'emailSuggestionCreatePerson',
      values: fields.flatMap((field) => {
        const value = text(values[field]);
        return value ? [[field, value] as [string, string]] : [];
      }),
    };
  }
  if (suggestion.kind === 'update_party_contact') {
    const changes = record(proposed['changes']);
    return {
      titleKey: 'emailSuggestionUpdateContact',
      titleParams: { name: text(proposed['partyName']) ?? '' },
      values: ['phone', 'email'].flatMap((field) => {
        const value = text(changes[field]);
        return value ? [[field, value] as [string, string]] : [];
      }),
    };
  }
  if (suggestion.kind === 'create_vehicle') {
    return {
      titleKey: 'emailSuggestionCreateVehicle',
      titleParams: { plate: text(proposed['registrationNumber']) ?? '' },
      values: present([
        ['vin', text(proposed['vin'])],
        ['mileage', numberText(proposed['mileage'])],
        ['owner', text(record(proposed['owner'])['partyName'])],
      ]),
    };
  }
  if (suggestion.kind === 'update_vehicle') {
    return {
      titleKey: 'emailSuggestionUpdateMileage',
      titleParams: { plate: text(proposed['registrationNumber']) ?? '' },
      values: present([['mileage', numberText(proposed['mileage'])]]),
    };
  }
  if (suggestion.kind === 'link_vehicle_party') {
    return {
      titleKey: 'emailSuggestionLinkVehicle',
      titleParams: { plate: text(proposed['registrationNumber']) ?? '' },
      values: present([['orderer', text(record(proposed['party'])['partyName'])]]),
    };
  }
  if (suggestion.kind === 'create_work_order') {
    return {
      titleKey: 'emailSuggestionCreateWorkOrder',
      titleParams: { plate: text(proposed['registrationNumber']) ?? '' },
      values: present([
        ['orderer', text(record(proposed['orderer'])['partyName'])],
        ['requestedWork', text(proposed['requestedWork'])],
        ['requestedTime', text(proposed['requestedTime'])],
      ]),
    };
  }
  return { titleKey: 'emailSuggestionAttach', values: [] };
}

/** A preview value as shown: the work order's time in the user's locale. */
function shown(field: string, value: string | null): string | null {
  return field === 'scheduledAt' && value !== null ? formatDateTimeShort(value) : value;
}

function SuggestionCard({
  conversationId,
  suggestion,
  onDecided,
}: {
  conversationId: string;
  suggestion: EmailSuggestion;
  onDecided?: ((decided: EmailSuggestionDecided) => void) | undefined;
}) {
  const { t } = useTranslation('messages');
  const notify = useNotify();
  const canApprove = useAuthStore((s) =>
    suggestion.requiredPermission ? s.hasPermission(suggestion.requiredPermission) : false,
  );
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const candidates = suggestion.kind === 'attach_to_work_order' ? candidatesOf(suggestion) : [];
  const [bookingId, setBookingId] = useState<string>(candidates.length === 1 ? (candidates[0]?.bookingId ?? '') : '');
  const picksTime = suggestion.kind === 'create_work_order';
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);
  const validTime = scheduledAt !== null && !Number.isNaN(scheduledAt.getTime());
  const base = `/conversations/${conversationId}/suggestions/${suggestion.id}`;
  const { titleKey, titleParams, values } = describe(suggestion);

  const decided = (outcome: EmailSuggestionOutcome) =>
    onDecided?.({ suggestionId: suggestion.id, kind: suggestion.kind, outcome });

  const failed = (error: unknown) => {
    const code = error instanceof ApiError ? (record(error.data)['code'] as unknown) : null;
    if (code === 'SUGGESTION_EXPIRED' || code === 'ALREADY_DECIDED') {
      notify.error(t('emailSuggestionExpired'));
      decided('expired');
      return;
    }
    if (code === 'PLAN_INVALID') {
      notify.error(t('emailSuggestionPreviewAgain'));
      setPreview(null);
      return;
    }
    notify.error(t('emailSuggestionFailed'));
  };

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } catch (error) {
      failed(error);
    } finally {
      setBusy(false);
    }
  };

  const doPreview = () =>
    run(async () => {
      const choice = bookingId ? { bookingId } : picksTime && scheduledAt && validTime ? { scheduledAt: scheduledAt.toISOString() } : {};
      const result = await api.post<Preview>(`${base}/preview`, choice);
      setPreview({ planHash: result.planHash, fieldChanges: result.fieldChanges });
    });

  const doConfirm = () =>
    run(async () => {
      if (!preview) return;
      await api.post(`${base}/confirm`, { planHash: preview.planHash });
      notify.success(t('emailSuggestionAccepted'));
      decided('accepted');
    });

  const doReject = () =>
    run(async () => {
      await api.post(`${base}/reject`);
      decided('rejected');
    });

  const label = (field: string) => (FIELD_LABEL_KEYS[field] ? t(FIELD_LABEL_KEYS[field]) : field);
  const valueText = (value: string | null) => value ?? t('emailSuggestionNoValue');
  const accepted = suggestion.status === 'accepted';

  let body: ReactNode = null;
  if (preview) {
    body = (
      <Stack spacing={0.5} data-testid="email-suggestion-preview">
        <Typography variant="caption" color="text.secondary">
          {t('emailSuggestionPreviewTitle')}
        </Typography>
        {preview.fieldChanges.map((change, index) => (
          <Typography key={`${change.field}-${String(index)}`} variant="body2">
            {label(change.field)}: {change.from === null ? '' : `${change.from} → `}
            <strong>{valueText(shown(change.field, change.to))}</strong>
          </Typography>
        ))}
      </Stack>
    );
  } else if (suggestion.kind === 'attach_to_work_order' && !accepted) {
    body = (
      <RadioGroup value={bookingId} onChange={(event) => setBookingId(event.target.value)}>
        {candidates.map((candidate) => (
          <FormControlLabel
            key={candidate.bookingId}
            value={candidate.bookingId}
            control={<Radio size="small" />}
            label={[candidate.workOrderNumber ?? t('emailSuggestionUnnumbered'), candidate.registrationNumber]
              .filter(Boolean)
              .join(' · ')}
          />
        ))}
      </RadioGroup>
    );
  } else if (values.length > 0) {
    body = (
      <Stack spacing={0.25}>
        {values.map(([field, value]) => (
          <Typography key={field} variant="body2">
            {label(field)}: <strong>{value}</strong>
          </Typography>
        ))}
        {picksTime && !accepted && canApprove && (
          <Box sx={{ pt: 1 }}>
            <DateTimePicker
              label={t('emailSuggestionPickTime')}
              value={scheduledAt}
              onChange={(value: Date | null) => setScheduledAt(value)}
              ampm={false}
              minutesStep={15}
              slotProps={{ textField: { size: 'small', fullWidth: true }, openPickerIcon: { fontSize: 'small' } }}
            />
          </Box>
        )}
      </Stack>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }} data-testid={`email-suggestion-${suggestion.kind}`}>
      <Stack spacing={1}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LightbulbOutlinedIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
            {t(titleKey, titleParams)}
          </Typography>
          {accepted && <Chip size="small" color="success" label={t('emailSuggestionAcceptedLabel')} />}
        </Box>
        {body}
        {!preview && suggestion.source.length > 0 && (
          <Box sx={{ borderLeft: 2, borderColor: 'divider', pl: 1 }}>
            {suggestion.source.map((source) => (
              <Typography key={`${source.field}-${source.quote}`} variant="caption" color="text.secondary" component="div">
                {t('emailSuggestionSource')}: “{source.quote}”
              </Typography>
            ))}
          </Box>
        )}
        {!accepted && (
          <Stack direction="row" spacing={1}>
            {preview ? (
              <>
                <Button size="small" variant="contained" onClick={() => { void doConfirm(); }} disabled={busy}>
                  {t('emailSuggestionConfirm')}
                </Button>
                <Button size="small" onClick={() => setPreview(null)} disabled={busy}>
                  {t('emailSuggestionBack')}
                </Button>
              </>
            ) : (
              <>
                {canApprove && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => { void doPreview(); }}
                    disabled={
                      busy || (suggestion.kind === 'attach_to_work_order' && !bookingId) || (picksTime && !validTime)
                    }
                    startIcon={busy ? <CircularProgress size={14} /> : undefined}
                  >
                    {t('emailSuggestionApprove')}
                  </Button>
                )}
                <Button size="small" color="inherit" onClick={() => { void doReject(); }} disabled={busy}>
                  {t('emailSuggestionReject')}
                </Button>
              </>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

export function EmailSuggestions({
  conversationId,
  suggestions,
  onDecided,
}: {
  conversationId: string;
  suggestions: EmailSuggestion[];
  onDecided?: (decided: EmailSuggestionDecided) => void;
}) {
  const { t } = useTranslation('messages');
  const supported = suggestions.filter((s) => SUPPORTED_KINDS.has(s.kind));
  if (supported.length === 0) return null;
  return (
    <Stack spacing={1} data-testid="email-suggestions">
      <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
        {t('emailSuggestionsIntro')}
      </Alert>
      {supported.map((suggestion) => (
        <SuggestionCard key={suggestion.id} conversationId={conversationId} suggestion={suggestion} onDecided={onDecided} />
      ))}
    </Stack>
  );
}
